import React from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {NavLink, useHistory} from 'react-router-dom'
import {FormModelType, useForm} from 'reactjs-use-form'

import {Alert, Box, Link, Stack} from '@mui/material'
import TextField from '@mui/material/TextField'
import {Theme, useTheme} from '@mui/material/styles'
import {PGTypography} from '@progynyglobal/pg-ui-library'
import {enqueueSnackbar} from 'notistack'
import {makeStyles} from 'tss-react/mui'

import blueSideBackgroundTop from '@progyny/assets/images/side-banner-white-top.png'
import SubmitButton from '@progyny/components/Buttons/SubmitButton'
import useChangeLanguage from '@progyny/components/Navigation/useChangeLanguage'
import {PasswordField} from '@progyny/components/PasswordField'
import {SidepanelContainer} from '@progyny/components/SidepanelContainer'
import {pageTestIds} from '@progyny/const/pageTestIds'
import {MeDocument, useLoginMutation} from '@progyny/generated/graphql'
import {useResponsive} from '@progyny/hooks/useResponsive'
import {RouteEnum, useQuery, useRoutes} from '@progyny/routes'
import {useLoginCriteria} from '@progyny/store/loginStore'

import i18n from '@root/i18n'

const Login = () => {
  const {classes} = useStyles()
  const {t} = useTranslation()
  const [loginMutation, {loading}] = useLoginMutation()
  const {changeLanguage} = useChangeLanguage()
  const theme = useTheme()
  const {setLoggedInBy} = useLoginCriteria()

  const history = useHistory()
  const isDownSM = useResponsive('down', 'sm')

  const formModel: FormModelType = {
    email: {
      value: '',
      required: true,
    },
    password: {
      value: '',
      required: true,
    },
  }

  const {values, errors, handleOnSubmit, handleOnChange} = useForm(
    formModel,
    handleSubmit
  )

  const query = useQuery()
  const {goTo, getPath} = useRoutes()

  function handleSubmit() {
    loginMutation({
      variables: {email: values.email, password: values.password},
      refetchQueries: [{query: MeDocument}],
      awaitRefetchQueries: true,
    })
      .then(async (response) => {
        await changeLanguage(i18n.language)
        if (response?.data?.login?.loggedInBy) {
          setLoggedInBy(response?.data?.login?.loggedInBy)
        }
        if (response.data?.login?.errors?.length) {
          enqueueSnackbar({
            message: response.data.login.errors[0]?.messages,
            variant: 'warning',
          })
        } else {
          // Wait for Me query to refetch to get company information
          setTimeout(async () => {
            try {
              if (query.has('next')) {
                const next = decodeURI(query.get('next') as string)
                history.push(next)
              } else {
                history.push('/')
              }
            } catch (error) {
              console.error('Error during domain redirect check:', error)
              if (query.has('next')) {
                const next = decodeURI(query.get('next') as string)
                history.push(next)
              } else {
                history.push('/')
              }
            }
          }, 100)
        }
      })
      .catch((error) => {
        if (error.networkError && error.networkError.statusCode === 429) {
          enqueueSnackbar({
            message: t('too_many_requests'),
            variant: 'warning',
          })
        } else {
          console.error(error)
        }
      })
  }

  return (
    <Box data-testid={pageTestIds.login}>
      <SidepanelContainer
        hideSidePanel={isDownSM}
        styleProps={{
          backgroundPicSrc: blueSideBackgroundTop,
          backgroundColor: theme.palette.primary.main,
          logoColor: 'white',
        }}
        mainComponent={
          <Stack flexDirection='column' width='100%' alignItems='center'>
            <Box
              padding={3}
              width={isDownSM ? '100%' : 720}
              mb={isDownSM ? 3 : 6}
              sx={{
                backgroundColor: theme.palette.tertiary.main,
                borderRadius: 4,
              }}
            >
              <PGTypography
                textAlign={'left'}
                fontSize={16}
                fontWeight={600}
                mb={2}
                color={theme.palette.other.brandColors.primary}
              >
                <Trans i18nKey='navigation_welcome_to_new_progyny_member' />
              </PGTypography>
              <PGTypography fontSize={16} fontWeight={400} textAlign={'left'}>
                <Trans i18nKey='navigation_we_ve_reimagined_platform_with' />
              </PGTypography>
            </Box>
            <Box className={classes.formContainer}>
              <form onSubmit={handleOnSubmit}>
                <PGTypography
                  variant='header'
                  as='h1'
                  fontWeight={500}
                  className={classes.register}
                  textAlign='center'
                >
                  {t('welcome_progyny')}
                </PGTypography>

                {!isDownSM && (
                  <PGTypography
                    textAlign='center'
                    variant='subtitle'
                    className={classes.signUpLine}
                  >
                    {t('dont_have_account')}{' '}
                    <Link
                      component={NavLink}
                      to={getPath(RouteEnum.REGISTER_REQUEST_EMAIL)}
                      className={classes.noWrapLink}
                    >
                      {t('register_now')}
                    </Link>
                  </PGTypography>
                )}

                <TextField
                  variant='outlined'
                  margin='dense'
                  size={'small'}
                  label={t('register_request_email_company_email_address')}
                  placeholder={t('yourname@yourcompany.com')}
                  name='email'
                  value={values.email}
                  onChange={handleOnChange}
                  error={errors?.email.hasError}
                  helperText={t(errors?.email.message)}
                  autoComplete='email'
                  className={classes.textFiled}
                  fullWidth
                  {...(isDownSM ? {inputProps: {'data-testid': 'email'}} : {})}
                />

                <PasswordField
                  noInfo
                  onChange={handleOnChange}
                  errors={errors}
                  values={{password: values.password}}
                  className={classes.textFiled}
                  margin={'dense'}
                  label={t('password_field_password')}
                  inputProps={{
                    autoComplete: 'current-password',
                    ...(isDownSM ? {'data-testid': 'password'} : {}),
                  }}
                />
                <Box textAlign='left' mb={4}>
                  <Link
                    display='block'
                    onClick={() => goTo(RouteEnum.FORGET_PASSWORD)}
                    className={classes.forgotButton}
                    data-testid='login-forgot-password'
                  >
                    {t('login_forgot_password')}
                  </Link>
                </Box>
                {query.has('show-login') && (
                  <Box mb={3}>
                    <Alert severity={'warning'}>
                      <Trans i18nKey='login_please_login_to_verify_your' />
                    </Alert>
                  </Box>
                )}
                <Box
                  sx={
                    isDownSM
                      ? {
                          position: 'fixed',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          bgcolor: 'background.default',
                          borderTop: '1px solid',
                          borderColor: 'grey.300',
                          px: 2,
                          pt: 2,
                          pb: 4,
                          zIndex: (theme) => theme.zIndex.appBar,
                        }
                      : undefined
                  }
                >
                  <SubmitButton
                    type={'submit'}
                    loading={loading}
                    className={classes.button}
                    fullWidth
                  >
                    {t('login_log_in')}
                  </SubmitButton>

                  {isDownSM && (
                    <PGTypography mt={3} className={classes.signUpLine}>
                      {t('dont_have_account')}{' '}
                      <Link
                        component={NavLink}
                        to={getPath(RouteEnum.REGISTER_REQUEST_EMAIL)}
                        className={classes.noWrapLink}
                      >
                        {t('register_now')}
                      </Link>
                    </PGTypography>
                  )}
                </Box>
              </form>
            </Box>
          </Stack>
        }
      />
    </Box>
  )
}

export default Login

const useStyles = makeStyles()((theme: Theme) => ({
  formField: {
    marginTop: 30,
  },
  forgotPasswordButton: {
    textTransform: 'none',
    marginTop: 10,
    marginBottom: 20,
    alignSelf: 'flex-start',
    marginLeft: -8,
    fontWeight: 'bold',
  },
  formContainer: {
    textAlign: 'center',
    width: 420,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingBottom: theme.spacing(20),
    },
    '& h4': {
      fontWeight: 'normal',
    },
  },
  registerText: {
    fontSize: 14,
    color: 'gray',
    marginTop: 13,
  },
  logo: {
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(4),
  },
  register: {
    marginBottom: theme.spacing(2.5),
  },
  textFiled: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  button: {
    display: 'block',
  },
  h4Label: {
    fontWeight: 'normal',
  },
  forgotButtonContainer: {
    paddingBottom: theme.spacing(4),
  },
  forgotButton: {
    display: 'inline',
  },
  signUpLine: {
    paddingBottom: theme.spacing(4),
  },
  noWrapLink: {
    whiteSpace: 'nowrap',
  },
}))
